import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// The page scrolls inside <main>, which stays mounted between routes, so the
// browser can't manage scroll for us. New pages start at the top; back and
// forward return to where you were on that page. The home page always
// returns to where you left it, even through the HOME link.

const HOME = '/';
// Keys that scroll the page; other keys (like Cmd+[ for back) don't cancel.
const SCROLL_KEYS = new Set([
	'ArrowUp',
	'ArrowDown',
	'PageUp',
	'PageDown',
	'Home',
	'End',
	' ',
]);

// How long to keep re-applying a restored position while the page fills in.
export const RESTORE_TIMEOUT_MS = 1500;
// The position has to hold this long before we stop.
const STABLE_MS = 150;
// A timer, not requestAnimationFrame: rAF is paused in background tabs.
const TICK_MS = 16;

const restore = (main, y) => {
	const start = performance.now();
	let timer;
	let stableSince = null;

	// Re-apply whenever the page content changes (like the home page grid
	// rendering). Mutation callbacks aren't throttled in background tabs,
	// unlike timers, so this works even when the timer below is slowed.
	const observer = new MutationObserver(() => apply());

	// Only real scrolling cancels the restore. A trackpad swipe back sends
	// sideways wheel events after the navigation, and a mouse back button
	// sends a non-left click; neither should cancel it.
	const onWheel = (e) => {
		if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) stop();
	};
	const onMouseDown = (e) => {
		if (e.button === 0) stop();
	};
	const onKeyDown = (e) => {
		if (SCROLL_KEYS.has(e.key)) stop();
	};

	const stop = () => {
		clearTimeout(timer);
		observer.disconnect();
		main.removeEventListener('wheel', onWheel);
		main.removeEventListener('touchmove', stop);
		main.removeEventListener('mousedown', onMouseDown);
		window.removeEventListener('keydown', onKeyDown);
	};

	const apply = () => {
		if (performance.now() - start > RESTORE_TIMEOUT_MS) {
			stop();
			return;
		}

		main.scrollTop = y;
	};

	main.addEventListener('wheel', onWheel, { passive: true });
	main.addEventListener('touchmove', stop, { passive: true });
	main.addEventListener('mousedown', onMouseDown);
	window.addEventListener('keydown', onKeyDown);
	observer.observe(main, { childList: true, subtree: true, attributes: true });

	// Also tick on a timer for layout changes without DOM changes (images
	// finishing loading), until the position has held for a moment.
	const tick = () => {
		apply();

		const now = performance.now();

		if (Math.abs(main.scrollTop - y) < 2) stableSince ??= now;
		else stableSince = null;

		if (
			(stableSince !== null && now - stableSince >= STABLE_MS) ||
			now - start > RESTORE_TIMEOUT_MS
		) {
			stop();
			return;
		}

		timer = setTimeout(tick, TICK_MS);
	};

	tick();

	return stop;
};

export default () => {
	const { key, pathname } = useLocation();
	const navigationType = useNavigationType();
	const currentKey = useRef(key);
	const previousPathname = useRef(pathname);
	// Lives as long as the page: a full reload starts fresh.
	const positions = useRef(new Map()).current;
	const lastHomeScroll = useRef(null);

	useEffect(() => {
		const main = document.querySelector('main');
		if (!main) return;

		// Save before the page changes: by the time the route effect runs, the
		// new page has replaced the old one and the old position is lost.
		// Scroll events alone aren't enough (browsers skip them in background
		// tabs), so also save on clicks (capture runs before the router's
		// link handler) and on back/forward (before React re-renders).
		// popstate uses capture so it runs before the router's own listener.
		const save = () => {
			positions.set(currentKey.current, main.scrollTop);
			if (previousPathname.current === HOME)
				lastHomeScroll.current = main.scrollTop;
		};

		main.addEventListener('scroll', save, { passive: true });
		document.addEventListener('click', save, true);
		window.addEventListener('popstate', save, true);

		return () => {
			main.removeEventListener('scroll', save);
			document.removeEventListener('click', save, true);
			window.removeEventListener('popstate', save, true);
		};
	}, []);

	useLayoutEffect(() => {
		const changed = previousPathname.current !== pathname;
		const keyChanged = currentKey.current !== key;

		previousPathname.current = pathname;
		currentKey.current = key;

		const main = document.querySelector('main');
		if (!main || !keyChanged) return;

		if (navigationType === 'POP') return restore(main, positions.get(key) || 0);

		if (!changed) return;

		if (pathname === HOME && lastHomeScroll.current !== null)
			return restore(main, lastHomeScroll.current);

		main.scrollTop = 0;
	}, [key, pathname, navigationType]);

	return null;
};
